import type { InitializeOptions, Tracker } from './types';
import { initZanalytics } from './initZanalytics';
import { once } from 'es-toolkit';
import { identifyUser } from './identifyUser';

const identifyForBrand = (options: InitializeOptions) => {
  if (options.brand === 'zillow') {
    identifyUser();
  } else if (options.brand === 'hotpads') {
    identifyUser(options.userId);
  }
};

/**
 * Use instead of `initialize` in sub-apps: the host page owns the SDK and the user's identity, so this only
 * identifies when no host started the SDK. `BootstrapSubappBuilder` calls it for you.
 * @param options - options for initializing analytics
 */
export const initializeSubApp = once((options: InitializeOptions = { brand: 'zillow' }) => {
  if (initZanalytics(options.brand)) {
    identifyForBrand(options);
  }
});

export function defineAnalytics<
  Trackers extends Record<string, Tracker<any>>, // eslint-disable-line @typescript-eslint/no-explicit-any
>(trackers: Trackers) {
  /**
   * Calls initZanalytics once
   * @param options - options for initializing analytics
   */
  const initialize = once((options: InitializeOptions = { brand: 'zillow' }) => {
    // The order of operations below is important
    initZanalytics(options.brand);
    // identifies even when the host (e.g. page frame) already started the SDK, since it may not have identified the user
    identifyForBrand(options);
  });

  /**
   * Emit an event to a named tracker.
   * @param tracker tracker name
   * @param payload payload to emit
   */
  function emitEvent<Tracker extends keyof Trackers, Payload extends Exclude<Trackers[Tracker]['__payload'], null>>(
    tracker: Tracker,
    payload: Payload,
  ) {
    return trackers[tracker].track(String(tracker), payload);
  }

  /**
   * Create a function that emits an event to a named tracker, bound to that specific tracker
   * @param tracker tracker name
   * @returns
   */
  function createTrackEvent<Tracker extends keyof Trackers, Extensions = Record<string, unknown>>(tracker: Tracker) {
    return function <Payload extends Exclude<Trackers[Tracker]['__payload'], null> & Extensions>(payload: Payload) {
      return emitEvent(tracker, payload);
    };
  }

  /**
   * Extend the analytics object with additional trackers
   * @param extendedTrackers - The additional trackers to extend the analytics object with
   * @returns A new analytics object with the additional trackers
   */
  function extend<
    ExtendedTrackers extends Record<string, Tracker<any>>, // eslint-disable-line @typescript-eslint/no-explicit-any
  >(extendedTrackers: ExtendedTrackers) {
    return defineAnalytics<ExtendedTrackers & Trackers>({ ...trackers, ...extendedTrackers });
  }

  return { initialize, initializeSubApp, emitEvent, createTrackEvent, extend } as const;
}
